import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { UserPlus, ArrowLeft } from "lucide-react";
import DocumentManager from "../components/documentManager";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Types
interface FormData {
  fullName: string;
  email: string;
  alternateEmail: string;
  password: string;
  phone: string;
  designation: string;
  department: string;
  company: string;
  employeeType: "Employee" | "Intern" | "Part-Time";
  totalPaidLeaves: string;
  doj: string;
  dateOfBirth: string;
  panNumber: string;
  accountNumber: string;
  ifsc: string;
  bankName: string;
  branch: string;
}

interface DocumentFiles {
  offerLetter: File | null;
  contractLetter: File | null;
  nda: File | null;
  aadharCard: File | null;
  contract: File | null;
  panCard: File | null;
}

interface ExistingDocuments {
  offerLetter: string | null;
  contractLetter: string | null;
  nda: string | null;
  aadharCard: string | null;
  contract: string | null;
  panCard: string | null;
}

export default function AddEmployee() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingEmployee, setIsFetchingEmployee] = useState(false);
  const [error, setError] = useState("");
  const [documentErrors, setDocumentErrors] = useState<{
    [key: string]: string;
  }>({});

  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    alternateEmail: "",
    password: "",
    phone: "",
    designation: "",
    department: "",
    company: "",
    employeeType: "Employee",
    totalPaidLeaves: "",
    doj: "",
    dateOfBirth: "",
    panNumber: "",
    accountNumber: "",
    ifsc: "",
    bankName: "",
    branch: "",
  });

  const [documents, setDocuments] = useState<DocumentFiles>({
    offerLetter: null,
    contractLetter: null,
    nda: null,
    aadharCard: null,
    contract: null,
    panCard: null,
  });

  const [existingDocuments, setExistingDocuments] = useState<ExistingDocuments>(
    {
      offerLetter: null,
      contractLetter: null,
      nda: null,
      aadharCard: null,
      contract: null,
      panCard: null,
    }
  );

  // Fetch employee data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchEmployeeData(id);
    }
  }, [isEditMode, id]);

  const fetchEmployeeData = async (employeeId: string) => {
    try {
      setIsFetchingEmployee(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const employee = response.data;

      setFormData({
        fullName: employee.fullName || "",
        email: employee.email || "",
        alternateEmail: employee.alternateEmail || "",
        // Never load password from API — hashes must not be echoed into the form
        password: "",
        phone: employee.phone || "",
        designation: employee.designation || "",
        department: employee.department || "",
        company: employee.company || "",
        employeeType: employee.employeeType || "Employee",
        totalPaidLeaves: employee.totalPaidLeaves?.toString() || "",
        doj: employee.doj ? employee.doj.split("T")[0] : "",
        dateOfBirth: employee.dateOfBirth
          ? employee.dateOfBirth.split("T")[0]
          : "",
        panNumber: employee.panNumber || "",
        accountNumber: employee.accountNumber || "",
        ifsc: employee.ifsc || "",
        bankName: employee.bankName || "",
        branch: employee.branch || "",
      });

      // Set existing documents
      if (employee.documents) {
        setExistingDocuments({
          offerLetter: employee.documents.offerLetter || null,
          contractLetter: employee.documents.contractLetter || null,
          nda: employee.documents.nda || null,
          aadharCard: employee.documents.aadharCard || null,
          contract: employee.documents.contract || null,
          panCard: employee.documents.panCard || null,
        });
      }
    } catch (error: any) {
      console.error("Error fetching employee:", error);
      setError(
        error.response?.data?.message || "Failed to fetch employee data"
      );
    } finally {
      setIsFetchingEmployee(false);
    }
  };

  const getRequiredDocuments = (type: string) => {
    switch (type) {
      case "Intern":
        return ["offerLetter", "panCard", "nda", "aadharCard"]; // Added nda and aadharCard for Interns
      case "Part-Time":
        return ["nda", "contract", "panCard"]; // 🆕 Added panCard
      case "Employee":
      default:
        return [
          "offerLetter",
          "contractLetter",
          "nda",
          "aadharCard",
          "panCard",
        ]; // 🆕 Added panCard
    }
  };

  const shouldShowDocument = (documentType: string) => {
    const requiredDocs = getRequiredDocuments(formData.employeeType);
    return requiredDocs.includes(documentType);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (field: string, file: File | null) => {
    setDocuments((prev) => ({
      ...prev,
      [field]: file,
    }));

    if (documentErrors[field]) {
      setDocumentErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDeleteDocument = async (field: string) => {
    try {
      setExistingDocuments((prev) => ({
        ...prev,
        [field]: null,
      }));

      setDocuments((prev) => ({
        ...prev,
        [field]: null,
      }));
    } catch (error) {
      setDocumentErrors((prev) => ({
        ...prev,
        [field]: "Failed to delete document",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setDocumentErrors({});
    setIsLoading(true);

    try {
      if (!formData.fullName || !formData.email) {
        setError("Full Name and Email are required");
        return;
      }

      // Create FormData object for multipart/form-data
      const submitData = new FormData();

      // Add text fields — omit blank password on edit so existing hash is preserved
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "password" && isEditMode && !String(value).trim()) {
          return;
        }
        submitData.append(key, value);
      });

      // Add file fields (only files that are selected)
      Object.entries(documents).forEach(([key, file]) => {
        if (file) {
          submitData.append(key, file);
        }
      });

      const token = localStorage.getItem("authToken");
      const url = isEditMode
        ? `${process.env.REACT_APP_BACKEND_URL}api/employees/${id}`
        : `${process.env.REACT_APP_BACKEND_URL}api/employees`;

      await axios.post(url, submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      navigate("/employees", {
        state: {
          message: isEditMode
            ? `${formData.employeeType} updated successfully!`
            : `${formData.employeeType} added successfully!`,
        },
      });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setError(
        error.response?.data?.message ||
          `Failed to ${
            isEditMode ? "update" : "add"
          } ${formData.employeeType.toLowerCase()}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetchingEmployee) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner variant="ellipsis" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/employees")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <UserPlus className="h-6 w-6" />
              <h1 className="text-3xl font-bold">
                {isEditMode ? "Edit" : "Add"} Employee/Intern/Part-Time
              </h1>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Type Selection */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Type Selection</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  <Label htmlFor="employeeType">
                    Employee Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.employeeType}
                    onValueChange={(value) =>
                      handleInputChange("employeeType", value)
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employee">
                        Full-Time Employee
                      </SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                      <SelectItem value="Part-Time">
                        Part-Time Employee
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {formData.employeeType === "Intern" &&
                      "Interns typically need an offer letter"}
                    {formData.employeeType === "Part-Time" &&
                      "Part-time employees typically need NDA and contract documents"}
                    {formData.employeeType === "Employee" &&
                      "Full-time employees typically need offer letter, contract, NDA, and ID documents"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="fullName">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    placeholder="John Doe"
                    disabled={isLoading}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="email">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      placeholder="john.doe@example.com"
                      disabled={isLoading || isEditMode}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="alternateEmail">Alternate Email</Label>
                    <Input
                      id="alternateEmail"
                      type="email"
                      value={formData.alternateEmail}
                      onChange={(e) =>
                        handleInputChange("alternateEmail", e.target.value)
                      }
                      placeholder="john.personal@example.com"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {/* <p className="text-sm text-muted-foreground -mt-2">
                  Optional: Additional email address for notifications
                </p> */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="password">
                      Password{" "}
                      {!isEditMode && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      placeholder={
                        isEditMode
                          ? "Leave blank to keep current password"
                          : "Enter employee login password"
                      }
                      autoComplete="new-password"
                      disabled={isLoading}
                      required={!isEditMode}
                    />
                    <p className="text-xs text-muted-foreground">
                      {isEditMode
                        ? "Leave blank to keep the existing password, or enter a new one to reset it."
                        : "This password will be used by the employee to log in."}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                      placeholder="+1234567890"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="designation">Designation</Label>
                    <Input
                      id="designation"
                      value={formData.designation}
                      onChange={(e) =>
                        handleInputChange("designation", e.target.value)
                      }
                      placeholder="Software Engineer"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) =>
                        handleInputChange("department", e.target.value)
                      }
                      placeholder="Engineering"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="company">
                      Company <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.company}
                      onValueChange={(value) =>
                        handleInputChange("company", value)
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bot2Do Technologies Pvt. Ltd">
                          Bot2Do Technologies Pvt. Ltd
                        </SelectItem>
                        <SelectItem value="DevAnsh Digital Solutions LLP">
                          DevAnsh Digital Solutions LLP
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="totalPaidLeaves">Total Paid Leaves</Label>
                    <Input
                      id="totalPaidLeaves"
                      type="number"
                      value={formData.totalPaidLeaves}
                      onChange={(e) =>
                        handleInputChange("totalPaidLeaves", e.target.value)
                      }
                      placeholder="20"
                      disabled={isLoading}
                    />
                    <p className="text-sm text-muted-foreground">
                      Total annual paid leaves allocation
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="doj">Date of Joining</Label>
                    <Input
                      id="doj"
                      type="date"
                      value={formData.doj}
                      onChange={(e) => handleInputChange("doj", e.target.value)}
                      disabled={isLoading}
                    />
                  </div>

                  {(formData.employeeType === "Employee" ||
                    formData.employeeType === "Intern") && (
                    <div className="grid gap-2">
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) =>
                          handleInputChange("dateOfBirth", e.target.value)
                        }
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
                {/* {(formData.employeeType === "Employee" ||
                  formData.employeeType === "Intern") && (
                  <p className="text-sm text-muted-foreground -mt-2">
                    Date of Birth is required for{" "}
                    {formData.employeeType === "Employee"
                      ? "Full-Time Employees"
                      : "Interns"}
                  </p>
                )} */}
              </CardContent>
            </Card>

            {/* Bank Details - For Full-Time Employees and Interns */}
            {(formData.employeeType === "Employee" ||
              formData.employeeType === "Intern") && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Bank Details</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="panNumber">PAN Number</Label>
                      <Input
                        id="panNumber"
                        value={formData.panNumber}
                        onChange={(e) =>
                          handleInputChange("panNumber", e.target.value)
                        }
                        placeholder="ABCDE1234F"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="accountNumber">Account Number</Label>
                      <Input
                        id="accountNumber"
                        value={formData.accountNumber}
                        onChange={(e) =>
                          handleInputChange("accountNumber", e.target.value)
                        }
                        placeholder="1234567890"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="ifsc">IFSC Code</Label>
                      <Input
                        id="ifsc"
                        value={formData.ifsc}
                        onChange={(e) =>
                          handleInputChange("ifsc", e.target.value)
                        }
                        placeholder="SBIN0001234"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="bankName">Bank Name</Label>
                      <Input
                        id="bankName"
                        value={formData.bankName}
                        onChange={(e) =>
                          handleInputChange("bankName", e.target.value)
                        }
                        placeholder="State Bank of India"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="branch">Branch</Label>
                    <Input
                      id="branch"
                      value={formData.branch}
                      onChange={(e) =>
                        handleInputChange("branch", e.target.value)
                      }
                      placeholder="Mumbai Main Branch"
                      disabled={isLoading}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents Upload - 2 Column Grid */}
            {/* Documents Upload - 2 Column Grid */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>
                  Documents Upload
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    (Optional)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shouldShowDocument("offerLetter") && (
                  <DocumentManager
                    label="Offer Letter"
                    field="offerLetter"
                    existingDocument={existingDocuments.offerLetter}
                    newFile={documents.offerLetter}
                    onFileChange={handleFileChange}
                    onDeleteDocument={handleDeleteDocument}
                    accept=".pdf,.jpg,.jpeg,.png"
                    isLoading={isLoading}
                    error={documentErrors.offerLetter}
                    uploadMode="manual"
                    documentType="document"
                    maxSizeMessage="Maximum file size: 10MB"
                    supportedFormatsMessage="Supported formats: PDF, JPG, PNG"
                  />
                )}

                {shouldShowDocument("contractLetter") && (
                  <DocumentManager
                    label="Contract Letter"
                    field="contractLetter"
                    existingDocument={existingDocuments.contractLetter}
                    newFile={documents.contractLetter}
                    onFileChange={handleFileChange}
                    onDeleteDocument={handleDeleteDocument}
                    accept=".pdf,.jpg,.jpeg,.png"
                    isLoading={isLoading}
                    error={documentErrors.contractLetter}
                    uploadMode="manual"
                    documentType="document"
                    maxSizeMessage="Maximum file size: 10MB"
                    supportedFormatsMessage="Supported formats: PDF, JPG, PNG"
                  />
                )}

                {shouldShowDocument("nda") && (
                  <DocumentManager
                    label="NDA Document"
                    field="nda"
                    existingDocument={existingDocuments.nda}
                    newFile={documents.nda}
                    onFileChange={handleFileChange}
                    onDeleteDocument={handleDeleteDocument}
                    accept=".pdf,.jpg,.jpeg,.png"
                    isLoading={isLoading}
                    error={documentErrors.nda}
                    uploadMode="manual"
                    documentType="document"
                    maxSizeMessage="Maximum file size: 10MB"
                    supportedFormatsMessage="Supported formats: PDF, JPG, PNG"
                  />
                )}

                {shouldShowDocument("contract") && (
                  <DocumentManager
                    label="Contract Document"
                    field="contract"
                    existingDocument={existingDocuments.contract}
                    newFile={documents.contract}
                    onFileChange={handleFileChange}
                    onDeleteDocument={handleDeleteDocument}
                    accept=".pdf,.jpg,.jpeg,.png"
                    isLoading={isLoading}
                    error={documentErrors.contract}
                    uploadMode="manual"
                    documentType="document"
                    maxSizeMessage="Maximum file size: 10MB"
                    supportedFormatsMessage="Supported formats: PDF, JPG, PNG"
                  />
                )}

                {shouldShowDocument("aadharCard") && (
                  <DocumentManager
                    label="Aadhar Card"
                    field="aadharCard"
                    existingDocument={existingDocuments.aadharCard}
                    newFile={documents.aadharCard}
                    onFileChange={handleFileChange}
                    onDeleteDocument={handleDeleteDocument}
                    accept=".pdf,.jpg,.jpeg,.png"
                    isLoading={isLoading}
                    error={documentErrors.aadharCard}
                    uploadMode="manual"
                    documentType="document"
                    maxSizeMessage="Maximum file size: 10MB"
                    supportedFormatsMessage="Supported formats: PDF, JPG, PNG"
                  />
                )}

                {/* 🆕 NEW: PAN Card Upload - For all employee types */}
                {shouldShowDocument("panCard") && (
                  <DocumentManager
                    label="PAN Card"
                    field="panCard"
                    existingDocument={existingDocuments.panCard}
                    newFile={documents.panCard}
                    onFileChange={handleFileChange}
                    onDeleteDocument={handleDeleteDocument}
                    accept=".pdf,.jpg,.jpeg,.png"
                    isLoading={isLoading}
                    error={documentErrors.panCard}
                    uploadMode="manual"
                    documentType="document"
                    maxSizeMessage="Maximum file size: 10MB"
                    supportedFormatsMessage="Supported formats: PDF, JPG, PNG"
                  />
                )}
              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/employees")}
                className="sm:w-auto"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} className="sm:w-auto">
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner variant="ellipsis" size={20} />
                    <span>
                      {isEditMode ? "Updating..." : "Adding..."}{" "}
                      {formData.employeeType}
                    </span>
                  </div>
                ) : (
                  <span>
                    {isEditMode ? "Update" : "Add"} {formData.employeeType}
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
